import { useMemo } from 'react'
import { IntegratedEditorColorScheme } from '@sparks-notation/react-editor/IntegratedEditor'
import { ThemeContextValue, useTheme } from './ThemeProvider'

/**
 * 把全站 token 翻译成编辑器内部的一套色槽。
 *
 * 为什么要有这层翻译：`packages/react-editor` 是 desktop 与 demo 共用的组件库，
 * 不认本站的 token（也不该认）；它只认自己那套语义槽位。这张表就是两者的接缝，
 * **改编辑器配色只改这里**，别去改组件库里的默认值 —— 那会连带改掉桌面版。
 *
 * 槽位取值照 `_v2-designs/jiangnan-qinan.html:1207-1216` 的「编辑器色槽」一表：
 *   voidary 一系 = 宣纸凹槽（面板/状态栏底、悬停、选中）
 *   positive 一系 = 青瓷（主按钮、激活态）
 *
 * 另外补了三样设计稿没列、但编辑器里绕不开的：
 *   `line` / `muted` —— 分隔线与次要文字。不补的话深色主题下用的是组件库的
 *                      `#0002` / `#333`，前者整片消失、后者黑字压深底。
 *   `code`         —— 代码编辑区。不补就还是 Ace 自带的 tomorrow（白底）。
 */
/**
 * 组件里用这个，别直接调 `editorColorScheme(useTheme())` ——
 * 编辑器把 colorScheme 当依赖去重建 JSS 表，每次渲染给个新对象就是每帧重建一张。
 * `theme` 只在明暗真正切换时才变，memo 到这里为止。
 */
export function useEditorColorScheme(): IntegratedEditorColorScheme {
	const theme = useTheme()
	return useMemo(() => editorColorScheme(theme), [theme])
}

export function editorColorScheme(t: ThemeContextValue): IntegratedEditorColorScheme {
	const p = t.palette
	const dark = t.resolved === 'dark'
	return {
		isDark: dark,
		voidary: p.paper3,
		// 凹槽的下一档。深浅主题都得和一个比 paper3 更远的台阶，不能共用一组字面量
		voidaryHover: dark ? '#2a3334' : '#dedcd3',
		voidarySelected: p.celadon2,
		voidaryActive: dark ? '#313b3c' : '#cdd8d3',

		positive: p.celadon,
		positiveHover: dark ? '#7fa79b' : '#5f8478',
		positiveActive: dark ? '#8fb6aa' : '#4e7065',

		// 主行动色 = 朱砂（「保存」主按钮的底色），跟弹/试听浮窗的倒数大字用它
		accent: p.cinnabar,

		line: p.line,
		muted: p.ink3,

		/**
		 * 谱面纸样 —— 设计稿 `.preview` 的 `--paper-2` 底 + `24px 26px` 内边距。
		 * 给了它预览面板才和首页那张样张是一个观感（纸底托着谱面，不贴边）。
		 * 谱面自身的墨色不在这里 —— 那由容器上的 `--ink` 经 `currentColor` 带下去。
		 */
		previewBackground: p.paper2,

		code: {
			isDark: dark,
			background: p.paper3,
			foreground: p.ink2,
			gutterForeground: p.ink3,
			// 行首命令：设计稿的 `.cmd` 取青瓷墨
			keyword: p.celadonInk,
			// 属性值与歌词：设计稿的 `.nx` 取墨
			value: p.ink,
			// 括号、竖线、延长号
			operator: p.ink3,
			// `//` 注释与无效行
			comment: p.ink3,
		},
	}
}
